import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Chrome, TelegramCard } from '@/shared/ui/Chrome'
import { Confirming } from '@/pages/payment/Confirming'
import { Details } from '@/pages/payment/Details'
import { Settled } from '@/pages/payment/Settled'
import { phaseOf, showsAddress } from '@/pages/payment/phase'
import { subtractDecimal } from '@/shared/lib/decimal'
import { PaymentForm } from '@/pages/payment/PaymentForm'
import { QrModal } from '@/pages/payment/QrModal'
import { TimerStrip, TopUpStrip } from '@/pages/payment/StatusStrip'
import { Button } from '@/shared/ui/Button'
import { Strip } from '@/shared/ui/Strip'
import styles from './Payment.module.css'
import { useCountdown } from '@/shared/lib/hooks/useCountdown'
import { usePaymentPage } from '@/shared/lib/hooks/usePaymentPage'
import { fiat } from '@/shared/lib/format'
import { explain } from '@/shared/lib/errors'
import { useMeta } from '@/shared/lib/hooks/useMeta'

export function PaymentPage() {
  useMeta('Оплата')
  const { invoiceId } = useParams<{ invoiceId: string }>()
  if (!invoiceId) {
    return (
      <Chrome>
        <Card>
          <Bad text="Нет номера заявки" />
        </Card>
      </Chrome>
    )
  }
  return <Loaded invoiceId={invoiceId} />
}

function Loaded({ invoiceId }: { invoiceId: string }) {
  const { page, loading, notFound, error, refresh, replace } = usePaymentPage(invoiceId)
  const [qrOpen, setQrOpen] = useState(false)
  // Хук вызывается всегда, до ранних возвратов: условный вызов запрещён
  // правилами хуков. Пока страницы нет — пустая строка даёт ноль.
  const secondsLeft = useCountdown(page?.expires_at ?? '')

  if (loading) {
    return <Chrome><Card><p className={styles.rowNote}>Загружаем заявку…</p></Card></Chrome>
  }

  if (notFound) {
    return (
      <Chrome>
        <Card>
          <Bad text="Заявка не найдена" />
        </Card>
        <TelegramCard />
      </Chrome>
    )
  }

  if (!page) {
    return (
      <Chrome>
        <Card>
          <Bad text={error ? explain(error) : 'Не удалось загрузить заявку'} />
          <Button block onClick={refresh}>Попробовать снова</Button>
        </Card>
        <TelegramCard />
      </Chrome>
    )
  }

  const payment = page.payment
  const phase = phaseOf(page.status, payment !== null)
  const withAddress = showsAddress(phase)

  // Остаток при недоплате. Строками: у Monero двенадцать знаков,
  // и разница, посчитанная в числах, отправит доплату не на ту сумму.
  const remaining = phase === 'topup' && payment
    ? subtractDecimal(payment.crypto_amount, payment.paid_amount)
    : null

  /*
    QR при доплате несёт ТОЛЬКО адрес.

    В `payment_uri` зашита полная сумма заявки, и отсканировав его,
    плательщик отправит её целиком второй раз. Переписать сумму
    в ссылке здесь нельзя: у трёх сетей три разных кодировки
    (BIP21 — десятичное, TON — целые нанотоны, Monero — tx_amount),
    и вторая реализация этих правил однажды разойдётся с бекендом.
    Пусть лучше кошелёк спросит сумму, чем подставит неверную.
  */
  // `payment_uri` может не прийти вовсе (у TRON и USDT-TON стандарта нет),
  // поэтому проверяем непустую строку, а не `!== null`.
  const uri = typeof payment?.payment_uri === 'string' && payment.payment_uri !== ''
    ? payment.payment_uri : null
  const qrValue = payment
    ? (phase === 'topup' ? payment.address : (uri ?? payment.address))
    : ''
  const qrHasAmount = phase !== 'topup' && uri !== null

  return (
    <Chrome>
      <Card>
        <div className={styles.stack}>
          <div className={styles.price}>
            {/*
              «Сумма к оплате» — пока эта цифра на экране одна
              (плательщик ещё выбирает сеть), и «Сумма заказа» —
              как только ниже появились реквизиты со ВТОРОЙ суммой,
              той же самой, но в криптовалюте. Две разные цифры под
              одинаковой подписью на одном экране — это перевод
              не той суммы.
            */}
            <span className={styles.priceLabel}>
              {withAddress ? 'Сумма заказа' : 'Сумма к оплате'}
            </span>
            <span className={styles.priceValue}>
              {fiat(page.amount_fiat, page.fiat_currency)}
            </span>
            {/*
              Та же сумма во второй валюте — справочно, со знаком «≈».
              Считает её бекенд по тем же котировкам, по которым выдаёт
              сумму к оплате: своя арифметика на клиенте показала бы
              на странице вторую правду о деньгах. Курса нет — строки
              просто нет.
            */}
            {page.amount_alt && page.alt_currency ? (
              <span className={styles.priceAlt}>
                ≈ {fiat(page.amount_alt, page.alt_currency)}
              </span>
            ) : null}
          </div>
          {/* Таймер только пока есть что оплачивать. На оплаченной
              заявке обратный отсчёт читается как «успейте заплатить». */}
          {phase === 'choose' || phase === 'awaiting' || phase === 'topup' ? (
            <TimerStrip secondsLeft={secondsLeft} expired={false} />
          ) : null}
        </div>

        {phase === 'settled' ? (
          <Settled status={page.status} payment={payment} invoiceId={page.id} />
        ) : null}

        {phase === 'confirming' ? <Confirming payment={payment} /> : null}

        {phase === 'topup' ? <TopUpStrip payment={payment} remaining={remaining} /> : null}

        {phase === 'choose' ? (
          <PaymentForm
            invoiceId={invoiceId}
            available={page.available_currencies}
            feeEstimates={page.network_fee_estimates ?? {}}
            onSelected={replace}
          />
        ) : null}

        {payment && withAddress ? (
          <div className={styles.stack}>
            <Details
              payment={payment}
              invoiceId={page.id}
              onShowQr={() => setQrOpen(true)}
              remaining={remaining}
            />
            <Strip tone="info">Вы платите комиссию сети</Strip>
          </div>
        ) : null}

        {/* Отказ сети не стирает реквизиты: адрес и сумма на экране
            по-прежнему верны, и перевод по ним дойдёт. Сообщаем о потере
            связи отдельно, чтобы человек понимал, почему замер статус. */}
        {error ? (
          <Strip tone="warn" aside={
              <Button variant="secondary" onClick={refresh}>Обновить</Button>
            }>
              Нет связи
            </Strip>
          ) : null}
      </Card>

      <TelegramCard />

      {qrOpen && payment && withAddress ? (
        <QrModal
          value={qrValue}
          withAmount={qrHasAmount}
          onClose={() => setQrOpen(false)}
        />
      ) : null}
    </Chrome>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className={styles.card}>{children}</div>
}

function Bad({ text }: { text: string }) {
  return (
    <Strip tone="bad">{text}</Strip>
  )
}
